import api, { PaginatedApiResponse, ApiResponse } from './axiosInstance';

export interface IProjectDocument {
    documentName: string;
    documentType: 'Manual' | 'Specification' | 'Certificate' | 'Warranty' | 'Installation' | 'Maintenance' | 'Other';
    documentFile: string;
    uploadedAt?: Date;
    uploadedBy?: string;
}

export interface AmsProjectListParams {
    search?: string;
    projectName?: string;
    projectId?: string;
    status?: string;
    approvalStatus?: string;
    projectStatus?: 'started';
    priority?: string;
    customerId?: string;
    locationId?: string;
    location?: string;
    projectTypeId?: string;
    requirement?: 'known' | 'unknown';
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    limit?: number;
    page?: number;
}

export interface CreateAmsProjectRequest {
    projectId: string;
    projectName: string;
    startDate: string; // DD/MM/YYYY or YYYY-MM-DD
    endDate?: string; // DD/MM/YYYY or YYYY-MM-DD
    lastmodifiedDate: string; // DD/MM/YYYY HH:MM AM/PM
    dateCreated: string; // DD/MM/YYYY HH:MM AM/PM
    projectManager: string;
    projectType: string;
    projectCategory: string;
    customerId: string;
    locationId?: string;
    projectTypeId: string;
    manager?: string;
    projectHead?: string;

    // AMS specific required fields
    requirement: 'known' | 'unknown';
    acCapacityMw?: number;
    dcCapacityMw?: number;

    // Assigned Resources
    assignedResources?: {
        projectManager?: string;
        auditors?: string[]; // Array of User ObjectIds for auditors
    };

    location?: string;
    latitude?: string;
    longitude?: string;
    name?: string;
    code?: string;
    attachedDocuments?: IProjectDocument[];
}

export interface UpdateAmsProjectRequest extends Partial<CreateAmsProjectRequest> { }

export const amsProjectService = {
    async getProjects(params?: AmsProjectListParams) {
        const response = await api.get<PaginatedApiResponse<any>>('/ams/ams-projects', { params });
        if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch AMS projects');
        return response.data;
    },

    async getProjectById(projectId: string) {
        const response = await api.get<ApiResponse<any>>(`/ams/ams-projects/${projectId}`);
        if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch AMS project');
        return response.data.data;
    },

    async createProject(payload: CreateAmsProjectRequest) {
        const response = await api.post<ApiResponse<any>>('/ams/ams-projects', payload);
        if (!response.data.success) throw new Error(response.data.message || 'Failed to create AMS project');
        return response.data.data;
    },

    async updateProject(projectId: string, payload: UpdateAmsProjectRequest) {
        const response = await api.patch<ApiResponse<any>>(`/ams/ams-projects/${projectId}`, payload);
        if (!response.data.success) throw new Error(response.data.message || 'Failed to update AMS project');
        return response.data.data;
    },

    async deleteProject(projectId: string) {
        const response = await api.delete<ApiResponse<null>>(`/ams/ams-projects/${projectId}`);
        if (!response.data.success) throw new Error(response.data.message || 'Failed to delete AMS project');
        return true;
    },

    // Project Resource Management
    async addProjectResources(projectId: string, resources: { auditors: string[] }) {
        const response = await api.patch<ApiResponse<any>>(`/ams/ams-projects/${projectId}/resources/add`, resources);
        if (!response.data.success) throw new Error(response.data.message || 'Failed to add auditors to AMS project');
        return response.data.data;
    },

    async removeProjectResources(projectId: string, resourceIds: string[]) {
        const response = await api.patch<ApiResponse<any>>(`/ams/ams-projects/${projectId}/resources/remove`, { resourceIds });
        if (!response.data.success) throw new Error(response.data.message || 'Failed to remove auditors from AMS project');
        return response.data.data;
    },

    async submitProjectForApproval(projectId: string, payload: { comments?: string }) {
        const response = await api.post<ApiResponse<any>>(`/ams/ams-projects/${projectId}/submit`, payload);
        if (!response.data.success) throw new Error(response.data.message || 'Failed to submit AMS project for approval');
        return response.data.data;
    },

    // Project-level approval functions
    // @deprecated AMS approves at the audit level; these project-level approval calls are
    // UI-removed and no longer invoked. Slated for removal (see
    // docs/specs/2026-07-13-ams-remove-project-approval-design.md §9 Phase 5).
    async approveProjectOverview(projectId: string, comments?: string) {
        const response = await api.post<ApiResponse<any>>(`/approvals/ams-project/${projectId}/approve-overview`, { comments });
        if (!response.data.success) throw new Error(response.data.message || 'Failed to approve project overview');
        return response.data;
    },

    async rejectProjectOverview(projectId: string, comments: string) {
        const response = await api.post<ApiResponse<any>>(`/approvals/ams-project/${projectId}/reject-overview`, { comments });
        if (!response.data.success) throw new Error(response.data.message || 'Failed to reject project overview');
        return response.data;
    },

    async approveAssetsAndEquipment(projectId: string, comments?: string) {
        const response = await api.post<ApiResponse<any>>(`/approvals/ams-project/${projectId}/approve-assets-equipment`, { comments });
        if (!response.data.success) throw new Error(response.data.message || 'Failed to approve assets & equipment');
        return response.data;
    },

    async rejectAssetsAndEquipment(projectId: string, comments: string) {
        const response = await api.post<ApiResponse<any>>(`/approvals/ams-project/${projectId}/reject-assets-equipment`, { comments });
        if (!response.data.success) throw new Error(response.data.message || 'Failed to reject assets & equipment');
        return response.data;
    },

    /** @deprecated AMS project-level submit — UI-removed; the audit-level bulk submit
     * (bulkSubmitAudits) replaces it. Slated for removal (design §9 Phase 5).
     * Submit project approval to manager (requester only). */
    async submitProjectApprovalToManager(projectId: string, payload?: { comments?: string }) {
        const response = await api.post<ApiResponse<any>>(`/approvals/ams-project/${projectId}/submit-approval-to-manager`, payload || {});
        if (!response.data.success) throw new Error(response.data.message || 'Failed to submit approval to manager');
        return response.data;
    },
};

export default amsProjectService;
